// OSS microservice API data transformation

import {
  digObject,
  getProp,
  getPropObject,
  strval,
  isNone,
  listSize,
  mergeObjects,
  isObject,
  copyProp,
  getTreeProp,
  lowerCase,
  hasProp,
  jsonStringify,
  upperCase,
} from "./util";
import { isResidential, isCondo } from "./features-config";
import getLogger from "./debug-logger";

const log = getLogger("oss-ms-transform", 3);

//----------------------------------------------------------
// extract/massage products before further transformations
//----------------------------------------------------------
export const extractMsResult = (respData) => {
  const data = digObject(respData, "Results", 0);
  const result = getPropObject(data, "Result") || {
    Message: "Dataset transformation failed",
  };
  copyProp("Success", data, result);
  delete result.Debug;
  // Unauthorized: message with list of excluded products, if any
  const unauth = getProp(respData, "Unauthorized");
  if (unauth) {
    result.Unauthorized = strval(unauth.split(":")[1]).trim();
  }
  return result;
};

//----------------------------------------------------------
// extract geocode from Address object
//----------------------------------------------------------
const extractAddressGeocode = (addrObj) => {
  const geocode = digObject(addrObj, "Geocodes", 0, "Geocode"); // as {Latitude, Longitude}
  if (isObject(geocode)) {
    const { Latitude: lat, Longitude: lng } = geocode;
    return { lat, lng };
  }
  return null;
};
//----------------------------------------------------------
// extract OAK (ID) and location from Address object (for Comparables)
//----------------------------------------------------------
const extractAddressLocation = (addrObj) => {
  const ret = {
    OptaAddressKey: getProp(addrObj, "ID"),
    StreetAddress: getProp(addrObj, "RequestedLine"),
  };
  const geocode = extractAddressGeocode(addrObj);
  mergeObjects(ret, geocode);
  return ret;
};

//----------------------------------------------------------
// extract PDF report URL from products
//----------------------------------------------------------
export const extractReportUrl = (resultset) =>
  digObject(resultset, "IClarifyResidentialReport", "Report", "Content", "ContentURL");

//----------------------------------------------------------
// transform single Comparable,
// translate microservice keys to those from OSS for backwards compatibility
//----------------------------------------------------------
const transformComparable = (compItem) => {
  const addr = digObject(compItem, "Addresses", 0);
  const building = digObject(compItem, "Building", 0);
  const result = {};

  result.DistanceFromSubjectProperty = getProp(compItem, "DistanceFromSubjectProperty");

  const propType = getProp(compItem, "PropertyTypeCode");

  log(compItem); //----------------- log

  // OptaAddressKey, StreetAddress, Latitude, Longitude
  mergeObjects(result, extractAddressLocation(addr));

  if (!isCondo(propType)) {
    result.LotSize = getProp(compItem, "LotSize");
    result.LotDepth = getProp(compItem, "LotDepth");
    result.LotFrontage = getProp(compItem, "LotFrontage");
  }
  // last listing and sold prices and dates
  const propValueItems = digObject(compItem, "PropertyValues", "AdditionalPropertyValueItems");
  if (listSize(propValueItems)) {
    propValueItems.forEach((it) => {
      const code = getProp(it, "PropertyValueTypeCode");

      if (code !== "SoldPrice" && code !== "ListingPrice") {
        return;
      }
      const item = digObject(it, "PropertyValueItems", 0); // use 1st array element only
      const val = getProp(item, "Value");
      const date = getProp(item, "Date");
      if (!isNone(date)) {
        result[code] = val;
        result[code.replace("Price", "Date")] = date;
      }
    });
    // setting Value same as SoldPrice for further data processing
    result.Value = result.SoldPrice;
  }

  result.ImageURL = digObject(building, "Images", 0, "ImageURL"); // property image

  log(`PropertyTypeCode "${propType}"`); //----------------- log

  let feats;
  if (isResidential(propType)) {
    feats = digObject(building, "ResidentialBuilding", "ResidentialConstructionFeatures");
    result.ArchitecturalStyleType = getProp(feats, "ArchitecturalStyleTypeCode");
    result.YearBuilt = getProp(feats, "YearBuilt");
    result.StoreyCount = digObject(feats, "BuildingStoreyConstruction", "StoreyCountTypeCode");
    // both TotalFloorArea and SquareFootage available, same value
    result.SquareFootage = getProp(feats, "SquareFootage");
    result.FinishedBasement = getProp(feats, "FinishedBasement");
    result.NumberOfBedrooms = digObject(feats, "BedroomConstruction", "NumberOfBedrooms");
    result.BathroomCount = digObject(feats, "BathroomConstruction", "BathroomCountTypeCode");
    result.ExteriorWallType = digObject(
      feats,
      "ExteriorWallConstructions",
      0,
      "ExteriorWallTypeCode"
    );
    result.GarageType = digObject(feats, "Garages", 0, "GarageTypeCode") || "";
    result.GarageNumberOfCars = digObject(feats, "Garages", 0, "NumberOfCars") || 0;
  } else if (isCondo(propType)) {
    const multiResBuilding = getProp(building, "MultiResidentialBuilding");
    result.MultiResidentialStyleType = getProp(multiResBuilding, "MultiResidentialStyleTypeCode");

    const buildingFeats = getProp(multiResBuilding, "CommercialConstructionFeatures");
    result.YearBuilt = getProp(buildingFeats, "YearBuilt");
    result.NumberOfStoreys = getProp(buildingFeats, "NumberOfStoreys");

    result.ParkingType = getTreeProp(buildingFeats, "ParkingTypeCode");

    const multiResUnit = getProp(multiResBuilding, "MultiResidentialUnit");
    result.FloorLevel = getProp(multiResUnit, "FloorLevel");

    const unitFeats = getProp(multiResUnit, "ResidentialConstructionFeatures");
    result.TotalFloorArea = getProp(unitFeats, "TotalFloorArea"); // same as SquareFootage
    result.NumberOfBathrooms = digObject(
      unitFeats,
      "BathroomConstruction",
      "BathroomCountTypeCode"
    );
    result.NumberOfBedrooms = digObject(unitFeats, "BedroomConstruction", "NumberOfBedrooms");
    result.NumberOfDens = digObject(unitFeats, "BedroomConstruction", "NumberOfDens");

    if (!result.TotalFloorArea && result.SquareFootage) {
      result.TotalFloorArea = result.SquareFootage;
    }

    if (!result.NumberOfStoreys && result.StoreyCount) {
      result.NumberOfStoreys = result.StoreyCount;
    }
  }
  log(`Comparable:`, result); //------------- log

  return result;
};

//----------------------------------------------------------
// extract and transform Comparables
//----------------------------------------------------------
export const extractComparables = (resultset) => {
  let comps = null;
  const props = digObject(resultset, "Properties", "Properties"); // array
  if (listSize(props)) {
    comps = props.map((it) => transformComparable(it));
  }
  return comps;
};

//----------------------------------------------------------
// transform single Rental Comparable,
// translate microservice keys to those from OSS for backwards compatibility
//----------------------------------------------------------
const transformRentalComparable = (compItem) => {
  const addr = digObject(compItem, "Addresses", 0);

  if (!addr) return null; // skip if no address info, should not happen

  const building = digObject(compItem, "Building", 0);

  const result = {};

  result.DistanceFromSubjectProperty = getProp(compItem, "DistanceFromSubjectProperty");

  // OptaAddressKey, StreetAddress, Latitude, Longitude
  mergeObjects(result, extractAddressLocation(addr));

  result.ImageURL = digObject(building, "Images", 0, "ImageURL"); // property image

  const residentialFeats = digObject(
    building,
    "ResidentialBuilding",
    "ResidentialConstructionFeatures"
  );

  if (residentialFeats) {
    result.YearBuilt = getProp(residentialFeats, "YearBuilt");
    result.SquareFootage = getProp(residentialFeats, "SquareFootage");
    result.NumberOfBedrooms = digObject(
      residentialFeats,
      "BedroomConstruction",
      "NumberOfBedrooms"
    );
    result.NumberOfBathrooms = digObject(
      residentialFeats,
      "BathroomConstruction",
      "BathroomCountTypeCode"
    );
  }

  const condoFeats = digObject(
    building,
    "MultiResidentialBuilding",
    "MultiResidentialUnit",
    "ResidentialConstructionFeatures"
  );

  if (condoFeats) {
    result.YearBuilt = digObject(
      building,
      "MultiResidentialBuilding",
      "CommercialConstructionFeatures",
      "YearBuilt"
    );
    result.SquareFootage = getProp(condoFeats, "SquareFootage");
    result.TotalFloorArea = getProp(condoFeats, "TotalFloorArea");
    result.NumberOfBedrooms = digObject(condoFeats, "BedroomConstruction", "NumberOfBedrooms");
    result.NumberOfBathrooms = digObject(
      condoFeats,
      "BathroomConstruction",
      "BathroomCountTypeCode"
    );
  }

  const valuation = digObject(compItem, "RentalValuation");

  if (valuation) {
    result.RentalAmount = getProp(valuation, "RentalAmount");
    result.RentalDate = getProp(valuation, "Date");
  }

  if (!result.TotalFloorArea && result.SquareFootage) {
    result.TotalFloorArea = result.SquareFootage;
  }

  if (!result.NumberOfStoreys && result.StoreyCount) {
    result.NumberOfStoreys = result.StoreyCount;
  }

  log(`Comparable:`, result); //------------- log

  return result;
};

//----------------------------------------------------------
// extract and transform Rental Comparables
//----------------------------------------------------------
export const extractRentalComparables = (resultset) => {
  let comps = null;
  const props = digObject(resultset, "RentalProperties", "Properties"); // array
  if (listSize(props)) {
    // check for error message in the first item, if so return null and set error message in global state
    const errorMsg = getProp(props[0], "ProductMessage");
    if (errorMsg) {
      log(`Rental comparables error: ${errorMsg}`); //---------------- log
      return null;
    }
    comps = props.map((it) => transformRentalComparable(it)).filter(Boolean); // filter out nulls just in case
  }
  return comps;
};

export const CRESTA = "CRESTA Accumulation Zone";

//----------------------------------------------------------
// extract Flood Map overlay URL
//----------------------------------------------------------
const transformFloodMaps = (resultset, updates = {}) => {
  const floodInfo = { 50: {}, 100: {}, 1000: {} };
  const floodMapItems = getTreeProp(resultset, "FloodMap"); // array is expected
  if (listSize(floodMapItems)) {
    floodMapItems.forEach((it) => {
      const code = getProp(it, "FloodEventCode");
      if (!(code in floodInfo)) return; // skip invalid codes
      if (hasProp(it, "FloodEventProbability")) {
        floodInfo[code].Probability = getProp(it, "FloodEventProbability");
      }
      const fc = getProp(it, "FloodContent");
      const imgCode = getProp(fc, "ImageTypeCode") || "";
      if (imgCode.startsWith("Overlay")) {
        floodInfo[code][imgCode] = getProp(fc, "ContentURL");
      }
    });
    log("floodInfo", floodInfo); //------------------ log
    updates.FLOOD_INFO_JSON = jsonStringify(floodInfo);
  }
  return updates;
};

//----------------------------------------------------------
// extract and transform Wildfire score
//----------------------------------------------------------
export const transformEnvironmental = (resultset, updates = {}) => {
  // try Report URL
  const reportUrl = extractReportUrl(resultset);
  if (reportUrl) {
    updates.REPORT_URL = reportUrl;
  }
  // extract Earthquake risk data

  const eqScoreData = getTreeProp(resultset, "EarthquakeScore");
  updates.EARTHQUAKE_SCORE = getProp(eqScoreData, "EarthquakeScoreNationalRisk");
  updates.EARTHQUAKE_RISK = upperCase(getProp(eqScoreData, "EarthquakeScoreNationalRiskCategoryCode") || "N/A");
  updates.EARTHQUAKE_SOIL = upperCase(getProp(eqScoreData, "SoilTypeDescriptionCode") || "N/A");
  updates.EARTHQUAKE_LIQUEFACTION = upperCase(getProp(eqScoreData, "Liquefaction250yrDescriptionCode") || "N/A");
  updates.EARTHQUAKE_TSUNAMI =
    upperCase(getProp(eqScoreData, "TsunamiHazardScore250yrDescriptionCode") || "N/A");
  updates.EARTHQUAKE_LANDSLIDE = upperCase(getProp(eqScoreData, "LandslideDescriptionCode") || "N/A");
  updates.EARTHQUAKE_PGA = upperCase(getProp(eqScoreData, "PeakGroundAcceleration250yrDescriptionCode") || "N/A");

  // extract Wildfire Score and Risk
  const fireScoresArr = getTreeProp(resultset, "WildfireScores");
  if (listSize(fireScoresArr)) {
    fireScoresArr.forEach((obj) => {
      if (getProp(obj, "ScoreTypeCode") !== "RiskAdjusted") return; // ignore such entries
      const score = getProp(obj, "Score");
      const risk = lowerCase(getProp(obj, "SeverityCode"));
      if (!isNone(score)) updates.WILDFIRE_SCORE = score;
      if (!isNone(risk)) updates.WILDFIRE_RISK = risk;
    });
  }
  // extract Active Wildfires
  const activeWildfires = getTreeProp(resultset, "ActiveWildfires");
  if (hasProp(activeWildfires, "ActiveFires", "FireDanger")) {
    updates.ACTIVE_WILDFIRES_JSON = jsonStringify(activeWildfires);
  }

  // extract Flood Score
  const floodScore = digObject(resultset, "FloodScores", "FloodScore", 0, "FloodScore");
  if (isNone(floodScore)) {
    updates.FLOODSCORE_ERROR = "FloodScore not found";
  } else {
    updates.FLOOD_SCORE = floodScore;
  }
  const floodInfo = digObject(resultset, "FloodScores", "FloodMap");
  if (listSize(floodInfo)) {
    floodInfo.forEach((it) => {
      const code = getProp(it, "FloodEventCode");
      if (code === "50" || code === "100" || code === "1000") {
        if (hasProp(it, "FloodEventProbability")) {
          updates[`FLOOD_PROBABILITY_${code}`] = getProp(it, "FloodEventProbability");
        }
      }
    });
  }
  // extract Flood Map overlay URLs
  transformFloodMaps(resultset, updates);

  return updates;
};
